import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Button } from '@tempmee/react';
import { createEmptyUser, fetchUsers } from '../../../../actions/userActions';
import { createLoadingSelector } from '../../../../apis/selectors';
import history from '../../../../history';
import { getQueryParam, setQueryParams, updateQueryParam } from '../../../../utils';
import { ListPage } from '../../../commonComponents/ListPage';
import { Table } from '../../../commonComponents/Table/Table';
import { handleOnSort, ROWS_PER_PAGE } from '../../../commonComponents/Table/utils';
import { getUserProfileUrl, userListColumn } from '../../../commonComponents/user-column';
import { filterKeys } from '../utils';
import { Filters } from './Filters';

const initialActions = ['GET_ADMIN_RAM', 'GET_DSO_NAMES'];
const initialLoadingSelector = createLoadingSelector(initialActions);

const actions = ['FETCH_DENTIST', 'FETCH_DSO', 'FETCH_HYGIENIST', 'FETCH_UNFINISHED_USERS'];
const loadingSelector = createLoadingSelector(actions);

const useFilterState = (searchParams) => {
  const initialFilterState = filterKeys.reduce((acc, key) => {
    acc[key] =
      searchParams?.[key] ??
      getQueryParam(key) ??
      (key === 'page' ? 1 : key === 'order' ? 'desc' : '');
    return acc;
  }, {});

  return useState(initialFilterState);
};

export const CustomTable = ({
  title,
  data,
  count,
  searchParams,
  params,
  conditionalRowStyles,
  CustomFilters,
  omitColumns = [],
}) => {
  const [initialPageState, setInitialPageState] = useState(getQueryParam('page'));
  const dispatch = useDispatch();
  const location = useLocation();
  const initialLoading = useSelector((state) => initialLoadingSelector(state));
  const isLoading = useSelector((state) => loadingSelector(state));
  const [filter, setFilter] = useFilterState(searchParams);
  const totalPages = Math.ceil(count / ROWS_PER_PAGE);

  const handleFilterChange = (type, value) => {
    const typeParamValue = getQueryParam(type);
    if (typeParamValue === value) {
      return;
    }

    updateQueryParam(type, value);
    setFilter((prevState) => ({ ...prevState, [type]: value, page: 1 }));
  };

  const handleTableChange = (page) => {
    setFilter((prevState) => ({ ...prevState, page }));
    updateQueryParam('page', page);
  };

  const handleSort = (selectedColumn, sortDirection) => {
    const sortObj = handleOnSort(selectedColumn, sortDirection, setFilter, {
      page: filter.page,
      orderKey: 'order',
    });
    setQueryParams(sortObj, filterKeys);
  };

  useEffect(() => {
    dispatch(
      fetchUsers({
        ...filter,
        ...params,
      }),
    );
  }, [filter, dispatch, params]);

  useEffect(() => {
    setQueryParams(filter, filterKeys);
    setInitialPageState(filter.page);
  }, [filter, location]);

  const onRowClicked = (row) => {
    history.push(getUserProfileUrl(row));
  };

  const getColumn = () => userListColumn();

  return (
    <ListPage
      title={title}
      isLoading={isLoading}
      isInitialLoading={initialLoading}
      total={count}
      actions={(
        <>
          {params.user_type !== 'HYG' && <AddDNTButton />}
          {params.user_type !== 'DNT' && <AddHYGButton />}
        </>
      )}
      filters={(
        <Filters
          filter={filter}
          handleFilterChange={handleFilterChange}
          CustomFilters={CustomFilters}
        />
      )}
    >
      <Table
        filter={filter}
        initialPage={initialPageState}
        columns={getColumn()?.filter((column) => !omitColumns.includes(column.name))}
        data={data}
        noDataComponent="There are no users"
        isLoading={isLoading}
        totalPages={totalPages}
        paginationPerPage={ROWS_PER_PAGE}
        paginationTotalRows={count}
        showTotal={false}
        onSort={handleSort}
        onRowClicked={onRowClicked}
        onPageChange={handleTableChange}
        conditionalRowStyles={conditionalRowStyles}
      />
    </ListPage>
  );
};

const AddDNTButton = () => {
  const dispatch = useDispatch();

  return (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      onClick={() => dispatch(createEmptyUser('DNT'))}
    >
      Create Empty Dentist
    </Button>
  );
};

const AddHYGButton = () => {
  const dispatch = useDispatch();

  return (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      onClick={() => dispatch(createEmptyUser('HYG'))}
    >
      Create Empty Hygienist
    </Button>
  );
};
